import React from 'react';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { useCall } from '../../context/CallContext.tsx';

export const IncomingCallModal: React.FC = () => {
  const { incomingCall, acceptIncomingCall, declineIncomingCall } = useCall();

  if (!incomingCall) return null;

  const isAudioCall = incomingCall.callMode === 'audio';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in font-sans">
      <div className="w-full max-w-md bg-[#121417] text-white rounded-4xl border border-white/10 shadow-neo-float p-6 text-center relative overflow-hidden">
        {/* Animated pulse rings */}
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none opacity-20" />

        <div className="flex items-center justify-center space-x-2 mb-6">
          <div className="w-2.5 h-2.5 rounded-full bg-brand-500 animate-ping" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
            {isAudioCall ? 'Incoming Audio Call' : 'Incoming Video Call'}
          </span>
        </div>

        {/* Caller Avatar with glowing animation */}
        <div className="relative inline-block mx-auto mb-4">
          <div className="w-24 h-24 rounded-3xl overflow-hidden ring-4 ring-brand-500/40 shadow-2xl relative mx-auto bg-surface-800 flex items-center justify-center">
            {incomingCall.callerAvatar ? (
              <img
                src={incomingCall.callerAvatar}
                alt={incomingCall.callerName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-brand-600 flex items-center justify-center text-white text-3xl font-black">
                {incomingCall.callerName.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-tr from-[#0162fc] to-[#4b2efa] text-white flex items-center justify-center shadow-lg">
            {isAudioCall ? (
              <Phone className="w-4 h-4 stroke-[2.5]" />
            ) : (
              <Video className="w-4 h-4 stroke-[2.5]" />
            )}
          </div>
        </div>

        {/* Caller Info */}
        <h3 className="text-xl font-black text-white tracking-tight mb-1">
          {incomingCall.callerName}
        </h3>
        <p className="text-xs font-semibold text-white/60 mb-2">
          {incomingCall.callerRole || 'Team Member'}
        </p>
        <div className="inline-block px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-mono text-brand-400 mb-8">
          {incomingCall.title}
        </div>

        {/* Action Buttons */}
        <div className={`grid ${isAudioCall ? 'grid-cols-2' : 'grid-cols-3'} gap-2.5`}>
          {isAudioCall ? (
            <button
              onClick={() => acceptIncomingCall(false)}
              className="py-3.5 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs flex items-center justify-center space-x-2 shadow-pulse transition-all hover:scale-[1.03] active:scale-[0.98]"
            >
              <Phone className="w-4 h-4 stroke-[2.5]" />
              <span>Accept Audio</span>
            </button>
          ) : (
            <>
              <button
                onClick={() => acceptIncomingCall(true)}
                className="py-3.5 px-3 rounded-2xl bg-gradient-to-r from-[#0162fc] to-[#4b2efa] hover:opacity-95 text-white font-black text-xs flex items-center justify-center space-x-1.5 shadow-pulse transition-all hover:scale-[1.03] active:scale-[0.98]"
              >
                <Video className="w-4 h-4 stroke-[2.5]" />
                <span>Video</span>
              </button>
              <button
                onClick={() => acceptIncomingCall(false)}
                className="py-3.5 px-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs flex items-center justify-center space-x-1.5 shadow-neo transition-all hover:scale-[1.03] active:scale-[0.98]"
              >
                <Phone className="w-4 h-4 stroke-[2.5]" />
                <span>Audio Only</span>
              </button>
            </>
          )}

          <button
            onClick={declineIncomingCall}
            className="py-3.5 px-4 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs flex items-center justify-center space-x-2 shadow-neo transition-all hover:scale-[1.03] active:scale-[0.98]"
          >
            <PhoneOff className="w-4 h-4 stroke-[2.5]" />
            <span>Decline</span>
          </button>
        </div>
      </div>
    </div>
  );
};

